'client-only';

import {
  type MediaImage,
  type Image,
} from '@shopify/hydrogen-react/storefront-api-types';
import { type Schema } from '@thebeyondgroup/shopify-rich-text-renderer';
import { useCallback, useMemo } from 'react';

import { useProducts } from './useProducts';

export const useProduct = (productId: string) => {
  const { getProductById } = useProducts();
  const product = getProductById(productId);

  const metafield = useCallback(
    ({ key, namespace = 'custom' }: { key: string; namespace?: string }) => {
      return product?.metafields
        .filter((metafield) => metafield?.namespace === namespace)
        .find((metafield) => metafield?.key === key);
    },
    [product],
  );

  const activeIngredients = useMemo(() => {
    const data = metafield({ key: 'active_ingredients' });

    return (
      data?.references?.nodes.map((node) => ({
        //@ts-expect-error Bad typing from Shopify SDK
        title: node.fields.find((field) => field.key === 'title')
          ?.value as string,
        //@ts-expect-error Bad typing from Shopify SDK
        short_title: node.fields.find((field) => field.key === 'short_title')
          ?.value as string,
        //@ts-expect-error Bad typing from Shopify SDK
        thumbnail: node.fields.find((field) => field.key === 'thumbnail')
          ?.reference?.image as Image,
      })) || []
    );
  }, [metafield]);

  const inactiveIngredients = useMemo(() => {
    const data = metafield({
      key: 'inactive_ingredients',
      namespace: 'formulation',
    });

    return data ? JSON.parse(data.value as string) : [];
  }, [metafield]);

  const dialogCallout = useMemo(() => {
    const data = metafield({ key: 'dialog_callout' });

    return data?.value;
  }, [metafield]);

  const recommendationChip = useMemo(() => {
    const data = metafield({ key: 'recommendation_chip' });

    return data?.value;
  }, [metafield]);

  const isRx = useMemo(() => {
    const data = metafield({ key: 'isRx' });

    if (!data) {
      return false;
    }

    return data.value === 'true' || (data.value as unknown as boolean) === true;
  }, [metafield]);

  const shortDescription = useMemo<Schema | undefined>(() => {
    const data = metafield({ key: 'short_description' });

    return data?.value as unknown as Schema;
  }, [metafield]);

  const dialogDescription = useMemo<Schema | undefined>(() => {
    const data = metafield({ key: 'dialog_description' });

    return data?.value as unknown as Schema;
  }, [metafield]);

  const ingredientNotes = useMemo<Schema | undefined>(() => {
    const data = metafield({
      key: 'ingredient_notes',
      namespace: 'formulation',
    });

    return data?.value as unknown as Schema;
  }, [metafield]);

  const dialogHero = useMemo<Image | undefined>(() => {
    const data = metafield({ key: 'dialog_hero' });
    const ref = data?.reference as MediaImage;

    return ref?.image as Image;
  }, [metafield]);

  const reviews = useMemo(() => {
    const count = metafield({ key: 'count', namespace: 'reviews' });
    const average = metafield({ key: 'average', namespace: 'reviews' });

    if (!count || !average) return undefined;

    return {
      count: parseInt(count.value),
      average: parseFloat(average.value),
    };
  }, [metafield]);

  const displayTitle = useMemo(
    () => metafield({ key: 'display_title' })?.value || product?.title,
    [metafield, product?.title],
  );

  return {
    metafield,
    activeIngredients,
    inactiveIngredients,
    shortDescription,
    ingredientNotes,
    displayTitle,
    reviews,
    dialogHero,
    dialogDescription,
    dialogCallout,
    recommendationChip,
    isRx,
    product,
  };
};
